import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as Sentry from '@sentry/react';
import { buildSentryUserId } from '../services/sentry';
import type { LocalizationSettings } from '../services/localizationApi';
import type { BillingAccessSummary } from '../types/billing';
import { structuralEqual } from '../lib/structuralEqual';

/**
 * A tenant the current user belongs to. Mirrors the `memberships[]` entry the
 * server attaches to `/auth/login` and `/auth/switch-tenant` responses
 * (AB#1470). `UX_TenantMemberships_UserId_CompatSingle` keeps every user at
 * exactly one live membership today — the switcher this feeds only renders
 * once a second membership exists (AB#1711 removes that constraint).
 */
export interface TenantMembership {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  role: 'SuperAdmin' | 'TenantAdmin' | 'TenantMember';
  isDefault: boolean;
}

export interface AuthUser {
  email: string;
  fullName: string;
  role: 'SuperAdmin' | 'TenantAdmin' | 'TenantMember';
  tenantId: string | null;
  tenantSlug: string | null;
  activeModules: string[];
  permissions: string[];
  localizationSettings: LocalizationSettings;
  memberships: TenantMembership[];
  /** Null for SuperAdmin / a tenant-less user (docs/design/billing-access-gate.md). */
  billing?: BillingAccessSummary | null;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  /**
   * True while the startup refresh (restoreSession in lib/api.ts) is minting the in-memory access
   * token for a persisted session. Never persisted: it describes this page load only. Route guards
   * render a skeleton while it is set, so no protected request goes out without a token and no
   * guard redirects on a session that is still being restored.
   */
  sessionRestoring: boolean;
  // S5: Refresh token artık HttpOnly cookie ile sunucu tarafında tutulur; client state'inde yok.
  setAuth: (token: string, user: AuthUser) => void;
  setAccessToken: (token: string) => void;
  updateActiveModules: (modules: string[]) => void;
  setPermissions: (permissions: string[]) => void;
  setMemberships: (memberships: TenantMembership[]) => void;
  setLocalizationSettings: (settings: LocalizationSettings) => void;
  updateUserRole: (role: AuthUser['role']) => void;
  /** Null clears the gate — e.g. mirroring a refresh envelope for SuperAdmin / a tenant-less user. */
  setBillingAccess: (summary: BillingAccessSummary | null) => void;
  setSessionRestoring: (restoring: boolean) => void;
  logout: () => void;
}

/**
 * Writes one field of `user`, or returns the very same state when the value is structurally
 * unchanged. `tryRefresh` re-mirrors permissions/memberships/localization/billing on every silent
 * token rotation; without this guard each of those writes rebuilt `user`, so every component that
 * reads `user` re-rendered on every rotation even though nothing changed.
 */
function withUserField<K extends keyof AuthUser>(
  state: AuthState,
  key: K,
  value: AuthUser[K],
): AuthState | Pick<AuthState, 'user'> {
  if (!state.user || structuralEqual(state.user[key], value)) return state;
  return { user: { ...state.user, [key]: value } };
}

/**
 * Kullanıcı oturum durumu.
 * localStorage'a yalnızca kullanıcı profili (user, isAuthenticated) persist edilir.
 * Access token (token) kasıtlı olarak persist dışında tutulur — sadece bellek içinde yaşar.
 * Sayfa yenilemesinde token null olur; persist edilmiş oturum varsa main.tsx açılışta
 * restoreSession() (lib/api.ts) ile tek seferlik refresh başlatır ve bitene kadar route guard'ları
 * skeleton gösterir (`sessionRestoring`), böylece korumalı ilk istekler 401 almaz. Başlangıç
 * refresh'i ulaşılamazsa (`unavailable`) oturum korunur ve reaktif 401 -> refresh interceptor'ı
 * (FE-AUDIT-2026-04-26 / H-8) yedek olarak çalışmaya devam eder.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      sessionRestoring: false,

      setAuth: (token, user) => {
        // Composite id is non-PII: tenantId UUID or role sentinel. No name/email.
        Sentry.setUser({ id: buildSentryUserId(user.tenantId, user.role) });
        set({ token, user, isAuthenticated: true });
      },

      setAccessToken: (token) =>
        set({ token }),

      updateActiveModules: (modules) =>
        set((state) => withUserField(state, 'activeModules', modules)),

      setPermissions: (permissions) =>
        set((state) => withUserField(state, 'permissions', permissions)),

      setMemberships: (memberships) =>
        set((state) => withUserField(state, 'memberships', memberships)),

      setLocalizationSettings: (settings) =>
        set((state) => withUserField(state, 'localizationSettings', settings)),

      updateUserRole: (role) =>
        set((state) => withUserField(state, 'role', role)),

      // Written by: the 402 axios interceptor (lib/api.ts) when a tenant-scope request comes
      // back Blocked, tryRefresh() mirroring the refresh envelope's `billing` field on every
      // silent token rotation (the same treatment permissions/memberships/localizationSettings
      // already got), SubscriptionGuard's periodic poll while Blocked, and the post-purchase
      // refresh paths (BillingReturnPage, dormant purchase success) once billing/access reports
      // Entitled again — every direction flips SubscriptionGuard without waiting for the next
      // login/refresh round-trip.
      setBillingAccess: (summary) =>
        set((state) => withUserField(state, 'billing', summary)),

      setSessionRestoring: (restoring) => set({ sessionRestoring: restoring }),

      logout: () => {
        Sentry.setUser(null);
        set({ token: null, user: null, isAuthenticated: false });
      },
    }),
    {
      name: 'beetegre-auth',
      // Bumped for the billing access gate (2026-09-11): `user.billing` is a new persisted
      // field. Every prior release wrote state at version <= 1, so `migrate` below still runs
      // for every pre-existing localStorage entry on first load post-deploy.
      version: 2,
      // Whitelist: access token is intentionally excluded from persistence.
      // Only profile fields that are not security-sensitive are stored in localStorage.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      // AB#1471: pre-existing persisted state (version 0) has no `memberships` key on
      // `user` at all. Backfill an empty array rather than leaving it undefined — the
      // switcher's `memberships.length > 1` check must never throw for a logged-in user
      // who has not refreshed their token since this shipped.
      migrate: (persistedState, version) => {
        const state = persistedState as { user?: AuthUser | null; isAuthenticated?: boolean };
        if (version < 1 && state?.user && !Array.isArray(state.user.memberships)) {
          state.user = { ...state.user, memberships: [] };
        }
        // Pre-existing persisted state (version < 2) has no `billing` key at all. Backfill
        // null rather than leaving it undefined so SubscriptionGuard's `user.billing?.enforced`
        // check is deliberate-false rather than relying on optional chaining alone surviving
        // every future call site.
        if (version < 2 && state?.user && !('billing' in state.user)) {
          state.user = { ...state.user, billing: null };
        }
        return state;
      },
      // Persisted user objects created before the permissions feature lack a `permissions`
      // key entirely. The `?? []` guard in usePermissions keeps React stable but the stored
      // `user.permissions` remains undefined, causing has() to return false for TenantMembers
      // and silently hiding SettingsRail items until logout/login. This hook defaults the
      // array to [] at rehydration time so the in-memory store is always type-safe and
      // consistent with AuthUser without requiring a fresh login.
      //
      // Kept alongside `migrate` (not instead of it) as a second line of defence: `migrate`
      // only runs when the stored `version` differs from the current one, so a future field
      // addition that forgets to bump `version` would otherwise rehydrate silently broken.
      onRehydrateStorage: () => (state) => {
        if (state?.user && !Array.isArray(state.user.permissions)) {
          state.user = { ...state.user, permissions: [] };
        }
        if (state?.user && !Array.isArray(state.user.memberships)) {
          state.user = { ...state.user, memberships: [] };
        }
        if (state?.user && !('billing' in state.user)) {
          state.user = { ...state.user, billing: null };
        }
      },
    }
  )
);
